///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// GLM flashes as sparks riding the storm column's lid. Each rendered frame
/// shows the flashes inside the window before the scene clock, fading with
/// age, placed in block space at the cloud-top height the displayed IR
/// frame gives for their position, so eyewall bursts sit on the eyewall
/// tower and rainband strokes on the bands. Window = the 10-minute frame
/// cadence, so a scrub step through the time-lapse shows that step's own
/// flashes and a paused clock keeps its sparks. Additive, depth-tested
/// against the lid (storm_column_lightning.glsl).
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer, type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { BlendFactor, DrawMode } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StormCloudField } from "./StormCloudField"
import { boxHeightKm, insideColumn, latLonToBlock, type StormColumnLayout } from "./stormColumnLayout"
import { type StormColumnFlash } from "./stormColumnLightning"
import { cloudTopKm } from "./stormColumnStats"

/** Flashes younger than this (scene time) are shown, fading out with age */
export const cColumnLightningWindowMs = 600_000
/** Radiant energy range mapped to brightness (log10 joules), as the globe sparks */
const cLogEnergyFaint = -15
const cLogEnergyBright = -12.5
/** Quad half-size for a full-strength flash, logical pixels */
const cSizePx = 16
/** Cap per frame so an exceptionally active eyewall stays a sparkle */
const cMaxFlashesPerFrame = 4000
/** Sparks float this far (true km) over the cloud top they belong to */
const cLiftKm = 0.6
/** Without an IR frame yet, sparks ride at this fraction of the box height */
const cNoFieldHeightFraction = 0.85
// position (3) + corner (2) + strength (1), six vertices per flash
const cVertexFloats = 6
const cQuadCorners = [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1]

export class StormColumnLightningNode extends NodeBase {
	private readonly layout: StormColumnLayout
	private readonly geometry: LightningGeometry
	private readonly lightningRenderer: LightningRenderer
	// Flashes inside the footprint sorted by time, with block x/z and brightness precomputed
	private times: Float64Array = new Float64Array(0)
	private latLon: Float32Array = new Float32Array(0)
	private xz: Float32Array = new Float32Array(0)
	private strengths: Float32Array = new Float32Array(0)
	private field?: StormCloudField
	private builtForMs = Number.NaN
	private builtForField?: StormCloudField
	/** Flashes drawn in the last rebuild (diagnostics) */
	public visibleCount = 0

	constructor(context: EarthBrowserContext, layout: StormColumnLayout) {
		super()
		this.layout = layout
		this.geometry = new LightningGeometry(context)
		this.lightningRenderer = new LightningRenderer(context, this.geometry)
		this.renderer = this.lightningRenderer
		// After the lid (2) so the depth test sees the relief
		this.renderOrder = 3
	}

	public get count(): number {
		return this.times.length
	}

	public set opacity(value: number) {
		this.lightningRenderer.opacity.value = value
	}

	/** Replace the flash set (any order); only flashes inside the footprint are kept */
	public setFlashes(flashes: StormColumnFlash[]): void {
		const kept: Array<{ flash: StormColumnFlash; x: number; z: number }> = []
		for (const flash of flashes) {
			const p = latLonToBlock(this.layout, flash.lat, flash.lon)
			if (insideColumn(this.layout, p.x, p.z)) kept.push({ flash, x: p.x, z: p.z })
		}
		kept.sort((a, b) => a.flash.t - b.flash.t)
		this.times = new Float64Array(kept.length)
		this.latLon = new Float32Array(kept.length * 2)
		this.xz = new Float32Array(kept.length * 2)
		this.strengths = new Float32Array(kept.length)
		kept.forEach((k, i) => {
			this.times[i] = k.flash.t
			this.latLon[i * 2] = k.flash.lat
			this.latLon[i * 2 + 1] = k.flash.lon
			this.xz[i * 2] = k.x
			this.xz[i * 2 + 1] = k.z
			const logE = Math.log10(Math.max(k.flash.energy, 1e-18))
			this.strengths[i] = Math.min(1, Math.max(0.2, (logE - cLogEnergyFaint) / (cLogEnergyBright - cLogEnergyFaint)))
		})
		this.builtForMs = Number.NaN
	}

	/** Heights come from the frame on the lid; called by the column when it changes */
	public setField(field: StormCloudField | undefined): void {
		this.field = field
	}

	/** Rebuild for the scene clock when it or the frame moved; called from the column's render */
	public update(clockMs: number): void {
		if (clockMs === this.builtForMs && this.field === this.builtForField) return
		this.builtForMs = clockMs
		this.builtForField = this.field
		this.rebuild(clockMs)
	}

	/** Index of the first flash at or after t (binary search) */
	private lowerBound(t: number): number {
		let lo = 0
		let hi = this.times.length
		while (lo < hi) {
			const mid = (lo + hi) >> 1
			if (this.times[mid] < t) lo = mid + 1
			else hi = mid
		}
		return lo
	}

	private rebuild(nowMs: number): void {
		const start = this.lowerBound(nowMs - cColumnLightningWindowMs)
		const end = this.lowerBound(nowMs + 1)
		const first = end - start > cMaxFlashesPerFrame ? end - cMaxFlashesPerFrame : start
		const count = end - first
		this.visibleCount = Math.max(0, count)
		if (count <= 0) {
			this.geometry.update(new Float32Array(0))
			return
		}
		const scale = this.layout.heightScale
		const fallbackY = boxHeightKm(this.layout) * cNoFieldHeightFraction
		const field = this.field
		const vertices = new Float32Array(count * 6 * cVertexFloats)
		let dst = 0
		for (let i = first; i < end; i++) {
			const age = (nowMs - this.times[i]) / cColumnLightningWindowMs
			const strength = this.strengths[i] * Math.pow(1 - Math.min(1, Math.max(0, age)), 0.6)
			const x = this.xz[i * 2]
			const z = this.xz[i * 2 + 1]
			const y = field
				? (cloudTopKm(field.sample(this.latLon[i * 2], this.latLon[i * 2 + 1])) + cLiftKm) * scale
				: fallbackY
			for (let c = 0; c < 6; c++) {
				vertices[dst++] = x
				vertices[dst++] = y
				vertices[dst++] = z
				vertices[dst++] = cQuadCorners[c * 2]
				vertices[dst++] = cQuadCorners[c * 2 + 1]
				vertices[dst++] = strength
			}
		}
		this.geometry.update(vertices)
	}

	public override render(state: RenderState): RenderActionSet[] {
		return this.geometry.available ? super.render(state) : []
	}

	public override destroy(): void {
		this.geometry.destroy()
		super.destroy()
	}
}

/** Dynamic quad list: re-uploaded whenever the visible flash set changes */
class LightningGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public corner: VertexAttribute
	public strength: VertexAttribute
	public vertexBuffer: VertexBuffer
	public bounds = new cube(-1e4, 1e4)
	public boundingSphere = new sphere(1e4)
	public available: boolean = false

	constructor(context: EarthBrowserContext) {
		this.position = new VertexAttribute(context, 3)
		this.corner = new VertexAttribute(context, 2)
		this.strength = new VertexAttribute(context, 1)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.corner, this.strength])
	}

	public update(vertices: Float32Array): void {
		if (vertices.length === 0) {
			this.available = false
			return
		}
		this.vertexBuffer.uploadData(vertices)
		this.available = true
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

class LightningRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private viewport = new UniformDynamic<vec2>()
	private sizePx = new UniformDynamic<number>()
	public opacity = new UniformDynamic<number>(1)

	constructor(context: EarthBrowserContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_lightning"), geometry, [transparentMaterial])
		// Additive over the lid; tested against its depth so towers in front
		// hide the sparks behind them, never written so sparks don't occlude
		this.drawState.depthTest = true
		this.drawState.depthWrite = false
		this.drawState.cullFace = false
		this.drawState.blend = [BlendFactor.SrcAlpha, BlendFactor.One]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.viewport.value = new vec2(state.width, state.height)
		this.sizePx.value = cSizePx
	}
}
